"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports["default"] = exports.DROP = void 0;

var _asserts = require("../asserts.js");

var _obj = require("../obj.js");

/**
 * @module ol/structs/PriorityQueue
 */

/**
 * @type {number}
 */
var DROP = Infinity;
/**
 * @classdesc
 * Priority queue.
 *
 * The implementation is inspired from the Closure Library's Heap class and
 * Python's heapq module.
 *
 * See https://github.com/google/closure-library/blob/master/closure/goog/structs/heap.js
 * and https://hg.python.org/cpython/file/2.7/Lib/heapq.py.
 *
 * @template T
 */

exports.DROP = DROP;

var PriorityQueue =
/** @class */
function () {
  /**
   * @param {function(T): number} priorityFunction Priority function.
   * @param {function(T): string} keyFunction Key function.
   */
  function PriorityQueue(priorityFunction, keyFunction) {
    /**
     * @type {function(T): number}
     * @private
     */
    this.priorityFunction_ = priorityFunction;
    /**
     * @type {function(T): string}
     * @private
     */

    this.keyFunction_ = keyFunction;
    /**
     * @type {Array<T>}
     * @private
     */

    this.elements_ = [];
    /**
     * @type {Array<number>}
     * @private
     */

    this.priorities_ = [];
    /**
     * @type {!Object<string, boolean>}
     * @private
     */

    this.queuedElements_ = {};
  }
  /**
   * FIXME empty description for jsdoc
   */


  PriorityQueue.prototype.clear = function () {
    this.elements_.length = 0;
    this.priorities_.length = 0;
    (0, _obj.clear)(this.queuedElements_);
  };
  /**
   * Remove and return the highest-priority element. O(log N).
   * @return {T} Element.
   */


  PriorityQueue.prototype.dequeue = function () {
    var elements = this.elements_;
    var priorities = this.priorities_;
    var element = elements[0];

    if (elements.length == 1) {
      elements.length = 0;
      priorities.length = 0;
    } else {
      elements[0] = elements.pop();
      priorities[0] = priorities.pop();
      this.siftUp_(0);
    }

    var elementKey = this.keyFunction_(element);
    delete this.queuedElements_[elementKey];
    return element;
  };
  /**
   * Enqueue an element. O(log N).
   * @param {T} element Element.
   * @return {boolean} The element was added to the queue.
   */


  PriorityQueue.prototype.enqueue = function (element) {
    (0, _asserts.assert)(!(this.keyFunction_(element) in this.queuedElements_), 31); // Tried to enqueue an `element` that was already added to the queue

    var priority = this.priorityFunction_(element);

    if (priority != DROP) {
      this.elements_.push(element);
      this.priorities_.push(priority);
      this.queuedElements_[this.keyFunction_(element)] = true;
      this.siftDown_(0, this.elements_.length - 1);
      return true;
    }

    return false;
  };
  /**
   * @return {number} Count.
   */


  PriorityQueue.prototype.getCount = function () {
    return this.elements_.length;
  };
  /**
   * Gets the index of the left child of the node at the given index.
   * @param {number} index The index of the node to get the left child for.
   * @return {number} The index of the left child.
   * @private
   */


  PriorityQueue.prototype.getLeftChildIndex_ = function (index) {
    return index * 2 + 1;
  };
  /**
   * Gets the index of the right child of the node at the given index.
   * @param {number} index The index of the node to get the right child for.
   * @return {number} The index of the right child.
   * @private
   */


  PriorityQueue.prototype.getRightChildIndex_ = function (index) {
    return index * 2 + 2;
  };
  /**
   * Gets the index of the parent of the node at the given index.
   * @param {number} index The index of the node to get the parent for.
   * @return {number} The index of the parent.
   * @private
   */


  PriorityQueue.prototype.getParentIndex_ = function (index) {
    return index - 1 >> 1;
  };
  /**
   * Make this a heap. O(N).
   * @private
   */


  PriorityQueue.prototype.heapify_ = function () {
    var i;

    for (i = (this.elements_.length >> 1) - 1; i >= 0; i--) {
      this.siftUp_(i);
    }
  };
  /**
   * @return {boolean} Is empty.
   */


  PriorityQueue.prototype.isEmpty = function () {
    return this.elements_.length === 0;
  };
  /**
   * @param {string} key Key.
   * @return {boolean} Is key queued.
   */


  PriorityQueue.prototype.isKeyQueued = function (key) {
    return key in this.queuedElements_;
  };
  /**
   * @param {T} element Element.
   * @return {boolean} Is queued.
   */


  PriorityQueue.prototype.isQueued = function (element) {
    return this.isKeyQueued(this.keyFunction_(element));
  };
  /**
   * @param {number} index The index of the node to move down.
   * @private
   */


  PriorityQueue.prototype.siftUp_ = function (index) {
    var elements = this.elements_;
    var priorities = this.priorities_;
    var count = elements.length;
    var element = elements[index];
    var priority = priorities[index];
    var startIndex = index;

    while (index < count >> 1) {
      var lIndex = this.getLeftChildIndex_(index);
      var rIndex = this.getRightChildIndex_(index);
      var smallerChildIndex = rIndex < count && priorities[rIndex] < priorities[lIndex] ? rIndex : lIndex;
      elements[index] = elements[smallerChildIndex];
      priorities[index] = priorities[smallerChildIndex];
      index = smallerChildIndex;
    }

    elements[index] = element;
    priorities[index] = priority;
    this.siftDown_(startIndex, index);
  };
  /**
   * @param {number} startIndex The index of the root.
   * @param {number} index The index of the node to move up.
   * @private
   */


  PriorityQueue.prototype.siftDown_ = function (startIndex, index) {
    var elements = this.elements_;
    var priorities = this.priorities_;
    var element = elements[index];
    var priority = priorities[index];

    while (index > startIndex) {
      var parentIndex = this.getParentIndex_(index);

      if (priorities[parentIndex] > priority) {
        elements[index] = elements[parentIndex];
        priorities[index] = priorities[parentIndex];
        index = parentIndex;
      } else {
        break;
      }
    }

    elements[index] = element;
    priorities[index] = priority;
  };
  /**
   * FIXME empty description for jsdoc
   */


  PriorityQueue.prototype.reprioritize = function () {
    var priorityFunction = this.priorityFunction_;
    var elements = this.elements_;
    var priorities = this.priorities_;
    var index = 0;
    var n = elements.length;
    var element, i, priority;

    for (i = 0; i < n; ++i) {
      element = elements[i];
      priority = priorityFunction(element);

      if (priority == DROP) {
        delete this.queuedElements_[this.keyFunction_(element)];
      } else {
        priorities[index] = priority;
        elements[index++] = element;
      }
    }

    elements.length = index;
    priorities.length = index;
    this.heapify_();
  };

  return PriorityQueue;
}();

var _default = PriorityQueue;
exports["default"] = _default;
//# sourceMappingURL=data:application/json;charset=utf-8;base64,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